import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import Header from 'shared/components/Header';
import LoadingSpinner from 'shared/components/LoadingSpinner';
import Table from 'shared/components/Table';
import { tokens } from 'shared/theme';
import useTenantUserService from 'shared/api/services/tenantUserService';
import CreateTenantUsers from './CreateTenantUsers';
import { useAuth } from 'shared/hooks/useAuth';
import { canViewAllRecords } from 'shared/utils/canViewAllRecords';
import { buildUserDetailsPath } from 'shared/utils/tenantRoute';

const TenantUsers = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const { tenantId } = useParams();
  const location = useLocation();
  const { user } = useAuth();
  const canViewAll = canViewAllRecords(user);
  
  const tenantName = location.state?.tenantName ?? user?.data?.tenantName;

  const {
    users,
    totalRecords,
    fetchTenantUsers,
    createTenantUser,
    deleteTenantUser,
    isLoading,
  } = useTenantUserService(tenantId);

  const [page, setPage] = useState(0);
  const [size] = useState(10);
  const [showAll, setShowAll] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchTenantUsers(page, size, canViewAll && showAll ? null : 'ACTIVE');
  }, [page, size, showAll, canViewAll, fetchTenantUsers]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const refreshList = useCallback(() => {
    fetchTenantUsers(page, size, canViewAll && showAll ? null : 'ACTIVE');
  }, [fetchTenantUsers, page, size, showAll, canViewAll]);

  const handleView = useCallback(
    (row) => {
      if (!row?.id || !tenantId) return;
      navigate(buildUserDetailsPath(row.id, tenantId), {
        state: { registryTenantId: tenantId, tenantName },
      });
    },
    [navigate, tenantId, tenantName]
  );
  const handleDeleteClick = useCallback((row) => setDeleteTarget(row), []);

  const confirmDelete = async () => {
    if (!deleteTarget?.id) return;
    setDeleting(true);
    try {
      await deleteTenantUser(deleteTarget.id);
      toast.success('User deleted');
      setDeleteTarget(null);
      refreshList();
    } catch (error) {
      const err =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        'Failed to delete user';
      toast.error(typeof err === 'string' ? err : 'Failed to delete user');
    } finally {
      setDeleting(false);
    }
  };

  const columns = useMemo(
    () => [
      { field: 'name', headerName: 'Name', flex: 1, minWidth: 140 },
      { field: 'email', headerName: 'Email', flex: 1.2, minWidth: 180 },
      { field: 'phoneNumber', headerName: 'Phone', flex: 1, minWidth: 120 },
      {
        field: 'jobPosition',
        headerName: 'Job Position',
        flex: 1,
        minWidth: 140,
        valueGetter: (params) =>
          params.row?.jobPosition || params.row?.job_position || '',
      },
      { field: 'tenantName', headerName: 'Tenant', flex: 1, minWidth: 140 },
      { field: 'role', headerName: 'Role', width: 110 },
      { field: 'status', headerName: 'Status', width: 110 },
      { field: 'createdAt', headerName: 'Created', flex: 1, minWidth: 160 },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 110,
        sortable: false,
        filterable: false,
        hideable: false,
        renderCell: ({ row }) => {
          const deleted = String(row?.status || '').toUpperCase() === 'DELETED';
          return (
            <Box display="flex" alignItems="center" gap={0.25}>
              <Tooltip title="View">
                <IconButton
                  size="small"
                  color="success"
                  onClick={() => handleView(row)}
                  aria-label="View user"
                >
                  <VisibilityOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={deleted ? 'Already deleted' : 'Delete'}>
                <span>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleDeleteClick(row)}
                    disabled={deleted}
                    aria-label="Delete user"
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          );
        },
      },
    ],
    [handleView, handleDeleteClick]
  );

  if (!tenantId) {
    return (
      <Box m="20px">
        <Typography color="error" sx={{ mb: 2 }}>
          No tenant was selected. Open this page from Tenants (eye icon).
        </Typography>
        <Button
          variant="contained"
          color="secondary"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/tenants')}
        >
          Back to tenants
        </Button>
      </Box>
    );
  }

  return (
    <Box m="20px">
      <Dialog
        open={Boolean(deleteTarget)}
        onClose={() => !deleting && setDeleteTarget(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete user?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">Do you want to delete the user?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={confirmDelete}
            disabled={deleting}
          >
            {deleting ? 'Deleting…' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      <CreateTenantUsers
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSuccess={refreshList}
        createTenantUser={createTenantUser}
        tenantName={tenantName}
      />

      <Header
        title={`${tenantName ? `${tenantName} ` : ''}Tenant users`}
        subtitle=""
      />

      <Box
        mb={2}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        gap={1}
        flexWrap="wrap"
      >
        {canViewAll ? (
        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
          <Button
            color="secondary"
            variant={showAll ? 'contained' : 'outlined'}
            size="small"
            sx={{ fontSize: '13px', py: 0.75, px: 1.5 }}
            onClick={() => {
              setShowAll(true);
              setPage(0);
            }}
          >
            View all
          </Button>
          <Button
            color="secondary"
            variant={!showAll ? 'contained' : 'outlined'}
            size="small"
            sx={{ fontSize: '13px', py: 0.75, px: 1.5 }}
            onClick={() => {
              setShowAll(false);
              setPage(0);
            }}
          >
            Active only
          </Button>
        </Box>
        ) : (
          <Box />
        )}

        <Button
          color="secondary"
          variant="contained"
          size="small"
          sx={{ fontSize: '13px', py: 0.75, px: 1.5 }}
          onClick={() => setCreateOpen(true)}
        >
          Create user
        </Button>
      </Box>

      {isLoading ? (
        <LoadingSpinner message="Loading tenant users…" height="300px" />
      ) : (
        <Table
          tableId="platform.tenant-users"
          title={`${tenantName ? `${tenantName} ` : ''}Tenant users`}
          rows={users}
          columns={columns}
          totalRecords={totalRecords}
          size={size}
          page={page}
          handlePageChange={handlePageChange}
          colors={colors}
        />
      )}
    </Box>
  );
};

export default TenantUsers;
